import React, { useState } from 'react';

import { useToast } from '@chakra-ui/react';

import { deleteContacts, fetchPaginatedContacts } from 'api/contacts';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { washHub } from 'store/slices';
import { useWashHubListener } from 'hooks';
import { ContactStatusEnum } from 'types/user';

import { CustomerContactsProps } from './CustomerContacts.props';

import CustomerContactsView from './CustomerContacts.view';

const CustomerContactsContainer = (_: CustomerContactsProps) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [page, setPage] = useState<number>(1);
  const [archivedPage, setArchivedPage] = useState<number>(1);
  const [tabIndex, setTabIndex] = useState<number>(0);

  const washHubId = washHub.selectors.useSelectedWashHubId();
  const isWashHubIdValid = washHub.selectors.useIsWashHubIdValid();
  const queryKey = [`fetch-contacts-${washHubId}`, page];
  const archivedQueryKey = [
    `fetch-contacts-archived-${washHubId}`,
    archivedPage,
  ];

  const queryFn = () =>
    fetchPaginatedContacts({
      page,
      limit: 10,
      washHub: washHubId,
      status: ContactStatusEnum.ACTIVE,
    });

  const archivedQueryFn = () =>
    fetchPaginatedContacts({
      page: archivedPage,
      limit: 10,
      washHub: washHubId,
      status: ContactStatusEnum.ARCHIVED,
    });

  const options = {
    enabled: isWashHubIdValid,
    keepPreviousData: true,
  };

  const { data, isFetching, isLoading } = useQuery(queryKey, queryFn, options);

  const {
    data: archivedData,
    isFetching: isArchivedFetching,
    isLoading: isArchivedLoading,
  } = useQuery(archivedQueryKey, archivedQueryFn, options);

  const { mutate, isLoading: isDeleting } = useMutation(deleteContacts);

  useWashHubListener(() => {
    setPage(1);
    setArchivedPage(1);
  });

  const contacts = data?.items || [];
  const totalCount = data?.total || 0;
  const totalPages = data?.totalPages || 0;

  const archivedContacts = archivedData?.items || [];
  const archivedTotalCount = archivedData?.total || 0;
  const archivedTotalPages = archivedData?.totalPages || 0;

  const pagination = {
    page,
    setPage,
    totalCount,
    totalPages,
  };

  const archivedPagination = {
    page: archivedPage,
    setPage: setArchivedPage,
    totalCount: archivedTotalCount,
    totalPages: archivedTotalPages,
  };

  const onDelete = (ids: string[], _onSuccess: () => void) =>
    mutate(ids, {
      onSuccess: () => {
        toast({
          duration: 3000,
          title: 'Success',
          status: 'success',
          description: 'Customer Contacts Deleted',
        });

        queryClient.invalidateQueries(`fetch-contacts-${washHubId}`);
        queryClient.invalidateQueries(`fetch-contacts-archived-${washHubId}`);

        queryClient.invalidateQueries(`fetch-contacts-${washHubId}-select`, {
          exact: true,
        });

        _onSuccess();
      },
    });

  const _props = {
    contacts,
    archivedContacts,
    isLoading: isLoading || isFetching,
    isArchivedLoading: isArchivedLoading || isArchivedFetching,
    isDeleting,
    pagination,
    archivedPagination,
    onDelete,
    tabIndex,
    onTabChange: (index: number) => {
      setTabIndex(index);
    },
  };

  return <CustomerContactsView {..._props} />;
};

export default CustomerContactsContainer;
